import React, { Fragment, useState, ReactElement, useMemo } from "react";
import { sortBy } from "lodash";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { Button } from "../../../../../components/src/Button";
import { Alert, AlertVariant } from "../../../../../components/src/Alert";
import { Link } from "wouter";

import { recursivelyRemoveKey } from "../../../../../common/src";

import { useAlertTimeOut } from "../../../../../components/src/common/Utils";
import {
    SubmitButton,
    TextField,
    DateField,
    IntegerField,
    CheckBoxField,
    SelectField,
    MultipleSelectField
} from "../../../../../components/src/form";
import { useMutation } from "urql";
import { UPDATE_PARTY_FUND_INFO } from "../../../../../graphql/src/party/queries";
import { PartyPageData } from "../PartyPage";
import {
    EsgComponentEnum,
    FundReportComponentEnum,
    FundTypeEnum,
    FundReportEnum,
    CalendarNameEnum,
    CalendarNameEnumDescriptions,
    EsgComponentEnumDescriptions,
    FundReportComponentEnumDescriptions,
    FundReportEnumDescriptions
} from "../../../types.generated";
import { formikUrqlErrorFormater } from "../../../../../components/src/common/formik-urql-error-helper";

export const PartyFundInfoTab = ({ data, refetch }: { data: PartyPageData; refetch: () => void }): ReactElement => {
    const [_, updatePartyFundInfo] = useMutation(UPDATE_PARTY_FUND_INFO);
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    const currentParty = useMemo(() => {
        return data?.party;
    }, [data]);

    if (!currentParty) return <div></div>;

    const defaultClassDefinition = {
        dividendPaying: false,
        enabled: true,
        instrumentId: "",
        managementFee: "",
        managementFeeStruct: "FLAT",
        minimumInitialSubscription: "10000000",
        minimumSubscriptionAdded: "0",
        numberOfDecimalsNAVPerUnit: 4,
        numberOfDecimalsUnits: 4,
        redemptionFee: "0",
        sIFA_ID: "",
        sIFA_UNIT: "",
        shareClass: "",
        startDate: "",
        startNAVPerUnit: "",
        subscriptionFee: "",
        tradeCutOff: "10:00",
        earlyCloseTradeCutOff: "",
        sfdr: "",
        fxHedged: false,
        cleanClass: false,
        mainClass: false,
        carryOwnCostPossible: false
    };

    let clientOptions = data.clients.map((d) => ({ key: d._id, value: d.name }));
    clientOptions = [{ key: "000000000000000000000000", value: "None" }, ...sortBy(clientOptions, "value")];

    let custodianOptions = data.custodians.map((d) => ({ key: d._id, value: d.name }));
    custodianOptions = [{ key: "000000000000000000000000", value: "None" }, ...sortBy(custodianOptions, "value")];

    let transferAgentOptions = (data.transferAgents ?? []).map((d) => ({ key: d._id, value: d.name }));
    transferAgentOptions = [{ key: "000000000000000000000000", value: "None" }, ...sortBy(transferAgentOptions, "value")];

    const fxHedgeAccountOptions = [
        { key: "000000000000000000000000", value: "None" },
        ...currentParty.accounts.map((d) => ({ key: d._id, value: d.name }))
    ];

    const fundTypeOptions = [{ key: null, value: "None" }].concat(
        sortBy(
            Object.keys(FundTypeEnum).map((name) => {
                return { key: name, value: name };
            }),
            "value"
        )
    );

    return (
        <div id="partyfundinfoform" className="partyfundinfoform form mx-2">
            <div className="mb-2 flex flex-wrap mx-1">Fund specific information.</div>
            <Formik
                enableReinitialize={true}
                validateOnMount={true}
                initialValues={currentParty.fundInfo}
                validationSchema={Yup.object({
                    fundDescription: Yup.string().required().min(2),
                    fundType: Yup.string().required().min(2),
                    tradeFrequency: Yup.string().required().min(2),
                    fundManager: Yup.string().required().min(2),
                    createRedeemInstrumentId: Yup.string().required().min(2),
                    portfolio: Yup.string().required().min(2),
                    riskLevel: Yup.number().required()
                })}
                onSubmit={async (submitValues, { setSubmitting, setErrors }) => {
                    const input: any = recursivelyRemoveKey(submitValues, "__typename");

                    await updatePartyFundInfo({ partyId: currentParty._id, input })
                        .then((result) => {
                            if ("error" in result && result.error) {
                                const message = formikUrqlErrorFormater(result.error, setErrors);
                                setAlert({ color: "danger", visible: true, message });
                            } else {
                                setAlert({ color: "success", visible: true, message: `The party has been updated successfully!` });
                                refetch();
                            }
                        })
                        .catch((error) => {
                            setAlert({ color: "danger", visible: true, message: error.toString() });
                        })
                        .finally(() => {
                            setSubmitting(false);
                        });
                }}
            >
                {({ isSubmitting, values }) => {
                    return (
                        <Fragment>
                            <Form autoComplete="off">
                                <div className="flex flex-wrap -mx-2">
                                    {/* Column 1 */}
                                    <div className="w-full md:w-4/12 px-2 mb-2">
                                        <TextField
                                            name="fundDescription"
                                            label="Fund description*"
                                            type="text"
                                            className="w-full mb-2"
                                            disabled={isSubmitting}
                                        />
                                        <SelectField
                                            name="fundType"
                                            label="Fund type*"
                                            options={fundTypeOptions}
                                            className="w-full mb-2"
                                            disabled={isSubmitting}
                                        />
                                        <TextField
                                            name="fundManager"
                                            label="Fund manager*"
                                            type="text"
                                            className="w-full mb-2"
                                            disabled={isSubmitting}
                                        />
                                        <SelectField
                                            name="createRedeemInstrumentId"
                                            label="CreateRedeem instrument*"
                                            options={[
                                                { key: "000000000000000000000000", value: "None" },
                                                ...currentParty.instruments.map((d) => ({ key: d._id, value: d.name }))
                                            ]}
                                            className="w-full mb-2"
                                            disabled={isSubmitting}
                                            size={12}
                                        />
                                        <MultipleSelectField
                                            className="w-full mb-2"
                                            size={10}
                                            name="calendarNames"
                                            label={
                                                values && values.calendarNames && values.calendarNames.length ? (
                                                    <Link to={"/calendars/" + currentParty._id}>Calendar</Link>
                                                ) : (
                                                    "Calendar"
                                                )
                                            }
                                            options={Object.values(CalendarNameEnum)}
                                            tooltips={CalendarNameEnumDescriptions}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    {/* Column 2 */}
                                    <div className="w-full md:w-4/12 px-2 mb-2">
                                        <IntegerField
                                            className="w-full mb-2"
                                            name="riskLevel"
                                            label="Risk level KIID-category"
                                            disabled={isSubmitting}
                                            min={0}
                                            max={100}
                                        />
                                        <SelectField
                                            name="fundCompanyId"
                                            label={
                                                values && values.fundCompanyId ? (
                                                    <Link to={"/parties/" + values.fundCompanyId}>Fund company</Link>
                                                ) : (
                                                    "Fund company"
                                                )
                                            }
                                            options={clientOptions}
                                            className="w-full mb-2"
                                            disabled={isSubmitting}
                                        />
                                        <SelectField
                                            name="taId"
                                            label={
                                                <a
                                                    href={"https://www.investopedia.com/terms/t/transferagent.asp"}
                                                    target={"_blank"}
                                                    rel="noreferrer"
                                                >
                                                    {"Transfer agent"}
                                                </a>
                                            }
                                            options={transferAgentOptions}
                                            className="w-full mb-2"
                                            disabled={isSubmitting}
                                        />
                                        <TextField
                                            className="w-full mb-2"
                                            name="portfolio"
                                            label="Portfolio*"
                                            type="text"
                                            disabled={isSubmitting}
                                        />
                                        <MultipleSelectField
                                            className="w-full mb-2"
                                            size={10}
                                            name="esgComponents"
                                            label="Esg components"
                                            options={Object.values(EsgComponentEnum)}
                                            tooltips={EsgComponentEnumDescriptions}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    {/* Column 3 */}
                                    <div className="w-full md:w-4/12 px-2 mb-2">
                                        <TextField
                                            name="tradeFrequency"
                                            label="Trade frequency*"
                                            type="text"
                                            className="w-full mb-2"
                                            disabled={isSubmitting}
                                        />
                                        <SelectField
                                            name="depositaryId"
                                            label={
                                                <a
                                                    href={"https://www.investopedia.com/terms/d/depository.asp"}
                                                    target={"_blank"}
                                                    rel="noreferrer"
                                                >
                                                    {"Depositary"}
                                                </a>
                                            }
                                            options={custodianOptions}
                                            className="w-full mb-2"
                                            disabled={isSubmitting}
                                        />
                                        <SelectField
                                            name="custodianId"
                                            label={
                                                <a
                                                    href={"https://www.investopedia.com/terms/c/custodian.asp"}
                                                    target={"_blank"}
                                                    rel="noreferrer"
                                                >
                                                    {"Custodian"}
                                                </a>
                                            }
                                            options={custodianOptions}
                                            className="w-full mb-2"
                                            disabled={isSubmitting}
                                        />
                                        <DateField
                                            className="w-full mb-2"
                                            name={"endDate"}
                                            label="End date"
                                            disabled={isSubmitting}
                                            size={12}
                                        />
                                        <MultipleSelectField
                                            className="w-full mb-2"
                                            size={10}
                                            name="fundReportComponents"
                                            label="Fund report components"
                                            options={Object.values(FundReportComponentEnum)}
                                            tooltips={FundReportComponentEnumDescriptions}
                                            disabled={isSubmitting}
                                        />
                                        <MultipleSelectField
                                            className="w-full mb-2"
                                            size={5}
                                            name="fundReportTypes"
                                            label="Fund report types"
                                            options={Object.values(FundReportEnum)}
                                            tooltips={FundReportEnumDescriptions}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-wrap -mx-2">
                                    <div className="w-full px-2 mb-2">
                                        <label style={{ fontWeight: "bold" }}>Classes</label>
                                        <FieldArray
                                            name="classes"
                                            render={(arrayHelpers) => (
                                                <Fragment>
                                                    {values && values.classes && values.classes.length > 0 ? (
                                                        values.classes.map((item, index) => (
                                                            <div key={index} className="mb-2 flex flex-wrap -mx-2 w-full">
                                                                <div className="w-full md:w-10/12 lg:w-8/12 xl:w-6/12 px-2 mb-2">
                                                                    <fieldset>
                                                                        <legend>{index + 1}</legend>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <SelectField
                                                                                name={`classes[${index}].instrumentId`}
                                                                                label={
                                                                                    <Link to={"/instruments/" + item.instrumentId}>
                                                                                        Instrument
                                                                                    </Link>
                                                                                }
                                                                                options={data.fundInternals.map((d) => ({
                                                                                    key: d._id,
                                                                                    value: d.name
                                                                                }))}
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={isSubmitting}
                                                                                size={12}
                                                                            />
                                                                            <DateField
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                name={`classes[${index}].startDate`}
                                                                                label="Start date*"
                                                                                disabled={isSubmitting}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <TextField
                                                                                name={`classes[${index}].subscriptionFee`}
                                                                                label="Subscription fee*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`classes[${index}].managementFee`}
                                                                                label="Management fee*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <TextField
                                                                                name={`classes[${index}].redemptionFee`}
                                                                                label="Redemption fee*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`classes[${index}].managementFeeStruct`}
                                                                                label="Management fee struct*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <TextField
                                                                                name={`classes[${index}].startNAVPerUnit`}
                                                                                label="Start NAV per unit*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`classes[${index}].minimumInitialSubscription`}
                                                                                label="Minimum initial subscription*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <TextField
                                                                                name={`classes[${index}].sIFA_ID`}
                                                                                label="SIFA_ID*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`classes[${index}].minimumSubscriptionAdded`}
                                                                                label="Minimum subscription added*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <TextField
                                                                                name={`classes[${index}].tradeCutOff`}
                                                                                label="Trade cutoff*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`classes[${index}].earlyCloseTradeCutOff`}
                                                                                label="Early close trade cutoff"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <IntegerField
                                                                                name={`classes[${index}].numberOfDecimalsUnits`}
                                                                                label="Number of decimals units*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                                min={0}
                                                                                max={10000}
                                                                            />
                                                                            <IntegerField
                                                                                name={`classes[${index}].numberOfDecimalsNAVPerUnit`}
                                                                                label="Number of decimals NAV per unit*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                                min={0}
                                                                                max={10000}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <SelectField
                                                                                name={`classes[${index}].fxHedgeAccountId`}
                                                                                label={"Fx hedge account"}
                                                                                options={fxHedgeAccountOptions}
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={isSubmitting}
                                                                                size={fxHedgeAccountOptions.length}
                                                                            />
                                                                            <TextField
                                                                                name={`classes[${index}].sfdr`}
                                                                                label="SFDR"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={isSubmitting}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <TextField
                                                                                name={`classes[${index}].sIFA_UNIT`}
                                                                                label="SIFA_UNIT*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                            <TextField
                                                                                name={`classes[${index}].shareClass`}
                                                                                label="Share class*"
                                                                                className="w-full md:w-6/12 px-2 mb-2"
                                                                                disabled={false}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <CheckBoxField
                                                                                name={`classes[${index}].mainClass`}
                                                                                label="Main class"
                                                                                className="w-full md:w-6/12 px-2 mb-2 flex flex-row mt-4 justify-start"
                                                                                disabled={isSubmitting}
                                                                            />
                                                                            <CheckBoxField
                                                                                name={`classes[${index}].dividendPaying`}
                                                                                label="Dividend paying"
                                                                                className="w-full md:w-6/12 px-2 mb-2 flex flex-row mt-4 justify-start"
                                                                                disabled={isSubmitting}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <CheckBoxField
                                                                                name={`classes[${index}].enabled`}
                                                                                label="Enabled"
                                                                                className="w-full md:w-6/12 px-2 mb-2 flex flex-row mt-4 justify-start"
                                                                                disabled={isSubmitting}
                                                                            />
                                                                            <CheckBoxField
                                                                                name={`classes[${index}].fxHedged`}
                                                                                label="Fx hedged"
                                                                                className="w-full md:w-6/12 px-2 mb-2 flex flex-row mt-4 justify-start"
                                                                                disabled={isSubmitting}
                                                                            />
                                                                        </div>
                                                                        <div className="mb-4 w-full flex flex-wrap -mx-2">
                                                                            <CheckBoxField
                                                                                name={`classes[${index}].cleanClass`}
                                                                                label="Clean class"
                                                                                className="w-full md:w-6/12 px-2 mb-2 flex flex-row mt-4 justify-start"
                                                                                disabled={isSubmitting}
                                                                            />
                                                                            <CheckBoxField
                                                                                name={`classes[${index}].carryOwnCostPossible`}
                                                                                label="Carry own cost possible"
                                                                                className="w-full md:w-6/12 px-2 mb-2 flex flex-row mt-4 justify-start"
                                                                                disabled={isSubmitting}
                                                                            />
                                                                        </div>
                                                                    </fieldset>
                                                                </div>
                                                                <div className="w-full md:w-2/12 lg:w-2/12 xl:w-1/12 px-2 mb-2">
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        className="mr-1 mt-2"
                                                                        type="button"
                                                                        onClick={() => arrayHelpers.remove(index)}
                                                                    >
                                                                        -
                                                                    </Button>
                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        className="mt-2"
                                                                        type="button"
                                                                        onClick={() => arrayHelpers.insert(index, defaultClassDefinition)}
                                                                    >
                                                                        +
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            className="ml-1"
                                                            type="button"
                                                            onClick={() => arrayHelpers.push(defaultClassDefinition)}
                                                        >
                                                            +
                                                        </Button>
                                                    )}
                                                </Fragment>
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-wrap -mx-2">
                                    <div className="w-full px-2 mb-2">
                                        {alert.visible ? (
                                            <Alert style={{ marginTop: "10px" }} variant={alert.color} onClose={onDismissAlert} dismissible>
                                                {alert.message}
                                            </Alert>
                                        ) : null}
                                        <SubmitButton disabled={isSubmitting} label="Update" />
                                    </div>
                                </div>
                            </Form>
                        </Fragment>
                    );
                }}
            </Formik>
        </div>
    );
};
